import { useState, useEffect, useCallback } from "react";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "config/firebase";

const useComments = (docPath) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!docPath) return;

    const pathSegments = docPath.split("/");
    const commentsRef = collection(db, ...pathSegments, "comments");
    const q = query(commentsRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(commentsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [docPath]);

  const addComment = useCallback(
    async (user, text, parentId = null, replyToUserName = null) => {
      if (!docPath || !user || !text.trim()) return;

      try {
        const pathSegments = docPath.split("/");
        const commentsRef = collection(db, ...pathSegments, "comments");
        await addDoc(commentsRef, {
          docPath,
          userId: user.uid,
          userName:
            user.displayName || user.email?.split("@")[0] || "Anonymous",
          userImage: user.photoURL || null,
          text: text.trim(),
          parentId,
          replyToUserName,
          createdAt: serverTimestamp(),
          likes: [],
        });
      } catch (error) {
        console.error("Error adding comment:", error);
      }
    },
    [docPath],
  );

  const deleteComment = useCallback(
    async (commentId) => {
      if (!docPath || !commentId) return;
      try {
        const pathSegments = docPath.split("/");
        const commentRef = doc(db, ...pathSegments, "comments", commentId);
        await deleteDoc(commentRef);
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    },
    [docPath],
  );

  return {
    comments,
    loading,
    addComment,
    deleteComment,
  };
};

export default useComments;
